'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { CompactProductFragmentFragmentDoc } from '@packages/gql/generated/shopping/CompactProductFragmentFragmentDoc';
import { Price } from '@packages/modules/src/Price/Price';
import { useResponsiveAkamaiTemplate } from '@packages/modules/src/hooks/useResponsiveAkamaiTemplate/useResponsiveAkamaiTemplate';
import { TaxHint } from '../TaxHint';

/* GraphQL */ `
  fragment CompactProductFragment on Product {
    name
    price {
      ...ProductPriceFragment
    }
    mainMedia: media(limit: 1) {
      url
    }
    delivery {
      ...TaxHintFragment
    }
  }
`;

export type CompactProductProps = {
  /**
   * Price, media and deliveryType **data** from given product.
   */
  data: FragmentType<typeof CompactProductFragmentFragmentDoc>;
  /**
   * Whether to show product name
   * Shows maximal two lines of the name
   * @default false
   */
  withProductName?: boolean;

  /**
   * Whether to show tax hint
   * @default true
   */
  showTaxHint?: boolean;
};

/**
 * CompactProduct component displays the Price and Image of a given product in a condensed layout.
 * */
export const CompactProduct = ({
  data: compactProductData,
  withProductName = false,
  showTaxHint = true,
}: CompactProductProps) => {
  const { mainMedia, name, price, delivery } = getFragmentData(
    CompactProductFragmentFragmentDoc,
    compactProductData,
  );
  const { templateName, imageDimensions } = useResponsiveAkamaiTemplate('compact');

  return (
    <div className="flex flex-row items-start gap-1.5">
      <div className="relative h-[72px] w-[72px] lg:h-[88px] lg:w-[88px]">
        {mainMedia[0]?.url && (
          <Image
            role="presentation"
            src={`${mainMedia[0].url}?${templateName}`}
            unoptimized
            alt=""
            style={{
              objectFit: 'contain',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
            }}
            {...imageDimensions}
          />
        )}
      </div>
      <div>
        {withProductName && (
          <p className="text-size-h4 text-color-text-dark mb-0.5 line-clamp-2 block font-bold">
            {name}
          </p>
        )}
        <Price data={price} />
        {showTaxHint && <TaxHint data={delivery} />}
      </div>
    </div>
  );
};
